import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtLocal } from "@/lib/dates";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

/** Reschedule an opportunity visit (pass `opp`) or a task (pass `task`). */
export default function RescheduleDialog({ opp, task, onOpenChange }) {
  const qc = useQueryClient();
  const item = opp || task;
  const current = opp ? opp.visit_date : task?.due_at;
  const [date, setDate] = useState("");
  const [remark, setRemark] = useState("");
  useEffect(() => { setDate(""); setRemark(""); }, [item]);

  const mut = useMutation({
    mutationFn: async () =>
      (opp
        ? await api.post(`/opportunities/${opp.id}/reschedule`, { visit_date: date, remark })
        : await api.post(`/tasks/${task.id}/reschedule`, { due_at: date, remark })
      ).data,
    onSuccess: () => {
      toast.success(opp ? "Visit rescheduled" : "Task rescheduled");
      ["opportunities", "tasks", "dashboard", "lead"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onOpenChange(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not reschedule"),
  });

  return (
    <Dialog open={Boolean(item)} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-md" data-testid="reschedule-dialog">
        <DialogHeader><DialogTitle className="text-navy">Reschedule · {opp ? opp.name : task?.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="reschedule-current">
            Currently: <b>{fmtLocal(current)}</b>
          </div>
          <div>
            <Label className="mb-1 block text-xs text-slate-500">{opp ? "New visit date & time" : "New due date & time"}</Label>
            <Input data-testid="reschedule-date-input" type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-slate-500">Remark</Label>
            <Textarea data-testid="reschedule-remark-input" rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="e.g. Client did not show up" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reschedule-cancel-btn">Cancel</Button>
          <Button data-testid="reschedule-save-btn" className="bg-navy hover:brightness-110" disabled={!date || !remark.trim() || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? "Saving…" : "Reschedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VisitHistory({ history, testid, label = "visit history" }) {
  const [open, setOpen] = useState(false);
  if (!history?.length) return null;
  return (
    <div className="mt-2" data-testid={testid}>
      <button onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }} data-testid={`${testid}-toggle`} className="text-[11px] font-semibold text-navy hover:underline">
        {open ? "Hide" : "Show"} {label} ({history.length})
      </button>
      {open && (
        <ol className="mt-1.5 space-y-1.5 border-l-2 border-amber-200 pl-3">
          {[...history].reverse().map((h, i) => (
            <li key={i} className="text-[11px] text-slate-600">
              <div><span className="line-through text-slate-400">{fmtLocal(h.previous_visit_date || h.previous_due_at)}</span> → <b>{fmtLocal(h.new_visit_date || h.new_due_at)}</b></div>
              <div className="italic">“{h.remark}”</div>
              <div className="text-slate-400">{h.by_name} · {new Date(h.at).toLocaleString()}</div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
